/**
 * Utility functions for date formatting
 */

/**
 * Format a date string for display to the user
 * Automatically uses the user's locale and provides relative dates for better UX
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  // Check for invalid date
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // Calculate difference in days
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Use relative dates for better UX
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays === -1) {
    return 'Yesterday';
  } else if (diffDays > 1 && diffDays <= 7) {
    return `In ${diffDays} days`;
  } else if (diffDays < -1 && diffDays >= -7) {
    return `${Math.abs(diffDays)} days ago`;
  } else {
    // For dates further away, use locale-specific formatting
    // Check if user is in a day-first locale (Australia, UK, most of Europe)
    const testDate = new Date(2023, 11, 25); // Dec 25, 2023
    const testFormatted = testDate.toLocaleDateString();
    const isDayFirst = testFormatted.startsWith('25') || testFormatted.includes('/25/') || testFormatted.includes('.25.') || testFormatted.includes('-25-');
    
    if (isDayFirst) {
      // Day-first format for Australia, UK, most of Europe
      const day = date.getDate();
      const month = date.toLocaleDateString(undefined, { month: 'short' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    } else {
      // Month-first format for US
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};

/**
 * Format date for form inputs (YYYY-MM-DD format)
 * Uses local timezone to avoid off-by-one date issues
 */
export const formatDateForInput = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  // Check for invalid date
  if (isNaN(date.getTime())) return '';
  
  // Get local date components to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  const result = `${year}-${month}-${day}`;
  return result;
};

/**
 * Check if a date is overdue
 */
export const isOverdue = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return date < today;
};

/**
 * Check if a date is due soon (within next 3 days)
 */
export const isDueSoon = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  const threeDaysFromNow = new Date();
  threeDaysFromNow.setDate(today.getDate() + 3);
  
  today.setHours(0, 0, 0, 0);
  threeDaysFromNow.setHours(23, 59, 59, 999);
  
  return date >= today && date <= threeDaysFromNow;
};

/**
 * Check if a date is today
 */
export const isToday = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  
  return date.toDateString() === today.toDateString();
};

/**
 * Check if a date is tomorrow
 */
export const isTomorrow = (dateString: string): boolean => {
  const date = new Date(dateString);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  return date.toDateString() === tomorrow.toDateString();
};

/**
 * Check if a date falls on the coming weekend (Saturday or Sunday)
 * Only includes the very next Saturday and Sunday, not future weekends
 */
export const isThisWeekend = (dateString: string): boolean => {
  const date = new Date(dateString);
  const today = new Date();
  
  // Get the next Saturday
  const daysUntilSaturday = (6 - today.getDay()) % 7;
  const nextSaturday = new Date(today);
  if (daysUntilSaturday === 0) {
    // If today is Saturday, get next Saturday instead
    nextSaturday.setDate(today.getDate() + 7);
  } else {
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
  }
  
  // Get the next Sunday
  const nextSunday = new Date(nextSaturday);
  nextSunday.setDate(nextSaturday.getDate() + 1);
  
  const targetDate = date.toDateString();
  return targetDate === nextSaturday.toDateString() || targetDate === nextSunday.toDateString();
};

/**
 * Group todos by their due date into chronological sections
 */

// Helper sorters
const sortByRank = (a: any, b: any) => {
  // Lower rank comes first, fallback to id for stability
  if (a.rank !== undefined && b.rank !== undefined) {
    if (a.rank === b.rank) return (a.id || 0) - (b.id || 0);
    return a.rank - b.rank;
  }
  if (a.rank !== undefined) return -1;
  if (b.rank !== undefined) return 1;
  return (a.id || 0) - (b.id || 0);
};

const sortByDueDateAndId = (a: any, b: any) => {
  if (!a.dueDate && !b.dueDate) {
    return (a.id || 0) - (b.id || 0);
  }
  if (!a.dueDate) return 1;
  if (!b.dueDate) return -1;
  const dateDiff = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  if (dateDiff !== 0) return dateDiff;
  return (a.id || 0) - (b.id || 0);
};

const sortAlphabetical = (a: any, b: any) => {
  const aTitle = (a.title || '').toLowerCase();
  const bTitle = (b.title || '').toLowerCase();
  if (aTitle < bTitle) return -1;
  if (aTitle > bTitle) return 1;
  return (a.id || 0) - (b.id || 0);
};

const sortByCompletionDate = (a: any, b: any) => {
  // Sort by completion date, most recent first
  if (!a.completedAt && !b.completedAt) {
    return (a.id || 0) - (b.id || 0);
  }
  if (!a.completedAt) return 1;
  if (!b.completedAt) return -1;
  // Most recent first (reverse chronological)
  const dateDiff = new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
  if (dateDiff !== 0) return dateDiff;
  return (a.id || 0) - (b.id || 0);
};

/**
 * Group todos by their due date into chronological sections, with custom sorting
 * @param todos Array of todos
 * @param sortType 'rank' | 'dueDate' | 'alphabetical'
 */
export const groupTodosByDueDate = (todos: any[], sortType: 'rank' | 'dueDate' | 'alphabetical' = 'rank', completedTodos: any[] = []) => {
  const overdue: any[] = [];
  const today: any[] = [];
  const tomorrow: any[] = [];
  const thisWeekend: any[] = [];
  const later: any[] = [];
  const noDueDate: any[] = [];

  todos.forEach(todo => {
    if (!todo.dueDate) {
      noDueDate.push(todo);
    } else if (isOverdue(todo.dueDate)) {
      overdue.push(todo);
    } else if (isToday(todo.dueDate)) {
      today.push(todo);
    } else if (isTomorrow(todo.dueDate)) {
      tomorrow.push(todo);
    } else if (isThisWeekend(todo.dueDate)) {
      thisWeekend.push(todo);
    } else {
      later.push(todo);
    }
  });

  let sorter = sortByRank;
  if (sortType === 'dueDate') sorter = sortByDueDateAndId;
  if (sortType === 'alphabetical') sorter = sortAlphabetical;

  overdue.sort(sorter);
  today.sort(sorter);
  tomorrow.sort(sorter);
  thisWeekend.sort(sorter);
  later.sort(sorter);
  noDueDate.sort(sorter);
  const completed = completedTodos ? [...completedTodos] : [];
  // Sort completed todos by completion date, most recent first
  completed.sort(sortByCompletionDate);

  return {
    overdue,
    today,
    tomorrow,
    thisWeekend,
    later,
    noDueDate,
    completed
  };
};

/**
 * Format completion date for display in natural language
 */
export const formatCompletionDate = (completedAtString: string): string => {
  if (!completedAtString) return '';
  
  const completedDate = new Date(completedAtString);
  // Check for invalid date
  if (isNaN(completedDate.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
  
  // Calculate difference in days
  const diffTime = today.getTime() - completedDay.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'completed today';
  } else if (diffDays === 1) {
    return 'completed yesterday';
  } else if (diffDays > 1 && diffDays <= 7) {
    return `completed ${diffDays} days ago`;
  } else if (diffDays > 7 && diffDays <= 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? 'completed 1 week ago' : `completed ${weeks} weeks ago`;
  } else {
    // For older completions, show the actual date
    const month = completedDate.toLocaleDateString(undefined, { month: 'short' });
    const day = completedDate.getDate();
    const year = completedDate.getFullYear();
    const currentYear = now.getFullYear();
    
    if (year === currentYear) {
      return `completed ${month} ${day}`;
    } else {
      return `completed ${month} ${day}, ${year}`;
    }
  }
};

/**
 * Get local date string for today (YYYY-MM-DD)
 */
export function getTodayISO() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get local date string for yesterday (YYYY-MM-DD)
 */
export function getYesterdayISO() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get local date string for tomorrow (YYYY-MM-DD)
 */
export function getTomorrowISO() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get local date string for N days in the future (YYYY-MM-DD)
 */
export function getFutureISO(days: number) {
  const future = new Date();
  future.setDate(future.getDate() + days);
  const year = future.getFullYear();
  const month = String(future.getMonth() + 1).padStart(2, '0');
  const day = String(future.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get random date string for "later" section (8-30 days from today)
 * Uses same logic as snooze "Later" option
 */
export function getRandomLaterDate(): string {
  const minDays = 8; // At least 1 week + 1 day
  const maxDays = 30; // No more than 1 month
  const randomDays = Math.floor(Math.random() * (maxDays - minDays + 1)) + minDays;
  return getFutureISO(randomDays);
}

/**
 * Get the next weekend date (Saturday) as ISO string (YYYY-MM-DD)
 */
export function getThisWeekendISO(): string {
  const today = new Date();
  const daysUntilSaturday = (6 - today.getDay()) % 7;
  const nextSaturday = new Date(today);
  
  if (daysUntilSaturday === 0) {
    // If today is Saturday, get next Saturday instead
    nextSaturday.setDate(today.getDate() + 7);
  } else {
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
  }
  
  const year = nextSaturday.getFullYear();
  const month = String(nextSaturday.getMonth() + 1).padStart(2, '0');
  const day = String(nextSaturday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get next Monday date as ISO string (YYYY-MM-DD)
 */
export function getNextMondayISO(): string {
  const today = new Date();
  const daysUntilMonday = (8 - today.getDay()) % 7 || 7; // 0=Sunday, 1=Monday, etc. If today is Monday, get next Monday
  const nextMonday = new Date(today);
  nextMonday.setDate(today.getDate() + daysUntilMonday);
  
  const year = nextMonday.getFullYear();
  const month = String(nextMonday.getMonth() + 1).padStart(2, '0');
  const day = String(nextMonday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a task was completed today
 */
export const isCompletedToday = (completedAtString: string): boolean => {
  if (!completedAtString) return false;
  
  const completedDate = new Date(completedAtString);
  if (isNaN(completedDate.getTime())) return false;
  
  const today = new Date();
  const completedDay = new Date(completedDate.getFullYear(), completedDate.getMonth(), completedDate.getDate());
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  return completedDay.getTime() === todayDay.getTime();
};

/**
 * Format date for compact display in badges and inline elements
 */
export const formatCompactDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  // Calculate difference in days
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Use relative dates for immediate context
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays === -1) {
    return 'Yesterday';
  } else if (diffDays > 1 && diffDays <= 7) {
    return `${diffDays}d`;
  } else if (diffDays < -1 && diffDays >= -7) {
    return `${Math.abs(diffDays)}d ago`;
  } else {
    // For dates further away, use compact month/day format
    const month = date.toLocaleDateString(undefined, { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}`;
  }
};


